import { useEffect, useRef } from "react";
import { LayersControl, MapContainer, TileLayer, useMap } from "react-leaflet";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

interface AdjacentCropMapEditorProps {
  geometry: GeoJSON.Geometry | null;
  onGeometryChange?: (geometry: GeoJSON.Geometry | null) => void;
  editable?: boolean;
  referencePlots?: GeoJSON.FeatureCollection | null;
  controlPoints?: GeoJSON.FeatureCollection | null;
  /** Outer wrapper height. Defaults to 600px to preserve existing usages. */
  height?: number | string;
}

/**
 * Component to add drawing controls to the map (Point and Polygon)
 */
const DrawControls = ({
  onGeometryChange,
  geometry,
  layerRef,
}: {
  onGeometryChange?: (geometry: GeoJSON.Geometry | null) => void;
  geometry: GeoJSON.Geometry | null;
  layerRef?: React.MutableRefObject<L.FeatureGroup | null>;
}) => {
  const map = useMap();
  const { t } = useTranslation();
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  // Guardamos los callbacks en refs para que los handlers de leaflet-draw
  // no dependan de identidades cambiantes (evitando teardown del
  // drawControl a mitad de trazado).
  const onGeometryChangeRef = useRef(onGeometryChange);
  const tRef = useRef(t);
  onGeometryChangeRef.current = onGeometryChange;
  tRef.current = t;

  // Reconstituye la geometría de salida a partir de las layers vivas en
  // drawnItems. Si hay una sola polygon → Polygon. Si hay dos o más →
  // MultiPolygon. Si hay un solo marker → Point. Si está vacío → null.
  const buildGeometryFromLayers = (): GeoJSON.Geometry | null => {
    const layers = drawnItemsRef.current.getLayers();
    if (layers.length === 0) return null;

    const polygons: number[][][][] = [];
    let firstPoint: GeoJSON.Point | null = null;

    for (const l of layers) {
      const gj = (l as any).toGeoJSON?.();
      const g = gj?.geometry;
      if (!g) continue;
      if (g.type === "Polygon") {
        polygons.push(g.coordinates as number[][][]);
      } else if (g.type === "MultiPolygon") {
        for (const p of g.coordinates as number[][][][]) polygons.push(p);
      } else if (g.type === "Point" && !firstPoint) {
        firstPoint = g;
      }
    }

    if (polygons.length === 1) {
      return { type: "Polygon", coordinates: polygons[0] } as GeoJSON.Polygon;
    }
    if (polygons.length > 1) {
      return {
        type: "MultiPolygon",
        coordinates: polygons,
      } as GeoJSON.MultiPolygon;
    }
    return firstPoint;
  };

  // Estilo canónico del cultivo colindante — se reutiliza para pintar
  // tanto lo que dibuja el auditor como la geometría cargada de BBDD.
  const cropStyle: L.PathOptions = {
    color: "#722ed1",
    weight: 3,
    fillOpacity: 0.3,
    fillColor: "#722ed1",
  };

  // Añade a `drawnItems` un layer por cada polígono presente en
  // `geometry` (aceptando Polygon, MultiPolygon o Point). Se usa una vez
  // al montar el editor para que la geometría preexistente sea editable
  // con la herramienta "edit" de leaflet-draw.
  const seedFromGeometry = (g: GeoJSON.Geometry | null) => {
    const drawnItems = drawnItemsRef.current;
    drawnItems.clearLayers();
    if (!g) return;
    if (g.type === "Polygon") {
      const ring = (g as GeoJSON.Polygon).coordinates[0];
      const layer = L.polygon(
        ring.map((c) => [c[1], c[0]] as [number, number]),
        cropStyle,
      );
      drawnItems.addLayer(layer);
    } else if (g.type === "MultiPolygon") {
      for (const poly of (g as GeoJSON.MultiPolygon).coordinates) {
        const ring = poly[0];
        const layer = L.polygon(
          ring.map((c) => [c[1], c[0]] as [number, number]),
          cropStyle,
        );
        drawnItems.addLayer(layer);
      }
    } else if (g.type === "Point") {
      const coords = (g as GeoJSON.Point).coordinates;
      const layer = L.marker([coords[1], coords[0]]);
      drawnItems.addLayer(layer);
    }
  };

  useEffect(() => {
    const drawnItems = drawnItemsRef.current;

    if (layerRef) {
      layerRef.current = drawnItems;
    }

    map.addLayer(drawnItems);

    // Sembrar los layers de la geometría inicial (única vez al montar).
    // Los cambios posteriores del prop `geometry` no re-siembran para
    // no pisar la sesión de edición del usuario.
    seedFromGeometry(geometry);
    if (drawnItems.getLayers().length > 0) {
      const bounds = drawnItems.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      }
    }

    const drawControl = new L.Control.Draw({
      position: "topleft",
      edit: {
        featureGroup: drawnItems,
      },
      draw: {
        polygon: {
          // Habilitamos auto-intersección — con `false` leaflet-draw
          // rechaza silenciosamente los clicks que crean segmentos que
          // se cruzan, lo que en la práctica capa el polígono a 3
          // vértices en dibujos densos y confunde al auditor. Dejamos
          // que el operador dibuje libremente y el backend acepta la
          // forma tal cual.
          allowIntersection: true,
          showArea: true,
          metric: true,
          shapeOptions: cropStyle,
          // Con `repeatMode: true` la herramienta de polígono queda
          // armada tras terminar uno — el auditor puede encadenar
          // varias parcelas del mismo cultivo colindante sin volver a
          // pulsar el botón. Salir del modo se hace con ESC o clicando
          // otra herramienta.
          repeatMode: true,
        },
        marker: {
          icon: new L.Icon({
            iconUrl:
              "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
            iconRetinaUrl:
              "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
            shadowUrl:
              "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
          }),
        },
        polyline: false,
        rectangle: false,
        circle: false,
        circlemarker: false,
      },
    });

    map.addControl(drawControl);

    const onCreated = (event: any) => {
      const layer = event.layer;
      // Multi-polígono: apilamos en lugar de reemplazar. Un click en la
      // herramienta "delete" de leaflet-draw permite luego borrar
      // polígonos concretos sin perder el resto.
      drawnItems.addLayer(layer);
      onGeometryChangeRef.current?.(buildGeometryFromLayers());
      message.success(
        tRef.current("map.geometry_created") ||
          "Geometría añadida al cultivo",
      );
    };

    const onEdited = () => {
      onGeometryChangeRef.current?.(buildGeometryFromLayers());
      message.success(
        tRef.current("map.geometry_edited") ||
          "Geometría editada correctamente",
      );
    };

    const onDeleted = () => {
      onGeometryChangeRef.current?.(buildGeometryFromLayers());
      message.success(
        tRef.current("map.geometry_deleted") || "Geometría eliminada",
      );
    };

    map.on(L.Draw.Event.CREATED, onCreated);
    map.on(L.Draw.Event.EDITED, onEdited);
    map.on(L.Draw.Event.DELETED, onDeleted);

    return () => {
      map.removeControl(drawControl);
      map.removeLayer(drawnItems);
      map.off(L.Draw.Event.CREATED, onCreated);
      map.off(L.Draw.Event.EDITED, onEdited);
      map.off(L.Draw.Event.DELETED, onDeleted);
    };
    // Dependencias intencionalmente reducidas: `map` es estable (viene
    // de useMap), los callbacks se leen a través de refs. No incluimos
    // `geometry` para no re-inicializar el drawControl a mitad de
    // trazado cuando el estado cambie tras un CREATED/EDITED.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // No renderizamos GeometryDisplay: `seedFromGeometry` ya carga los
  // polígonos iniciales en drawnItems como layers editables.
  return null;
};

/**
 * Component to display reference plots from the farm
 */
const ReferencePlotsLayer = ({ 
  referencePlots,
  layerRef
}: { 
  referencePlots: GeoJSON.FeatureCollection | null;
  layerRef?: React.MutableRefObject<L.FeatureGroup | null>;
}) => {
  const map = useMap();
  const layerGroupRef = useRef<L.FeatureGroup>(new L.FeatureGroup());

  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    
    if (layerRef) {
      layerRef.current = layerGroup;
    }
    
    map.addLayer(layerGroup);

    return () => {
      map.removeLayer(layerGroup);
    };
  }, [map, layerRef]);

  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    layerGroup.clearLayers();

    if (!referencePlots || !referencePlots.features) return;

    referencePlots.features.forEach((feature) => {
      if (!feature.geometry) return;

      const geometry = feature.geometry;
      let layer: L.Layer | null = null;

      if (geometry.type === 'Polygon') {
        const coords = (geometry as any).coordinates[0];
        layer = L.polygon(
          coords.map((coord: number[]) => [coord[1], coord[0]]),
          {
            color: '#1890ff',
            weight: 2,
            fillOpacity: 0.1,
            fillColor: '#1890ff',
          }
        );
      } else if (geometry.type === 'MultiPolygon') {
        const coords = (geometry as any).coordinates;
        const polygons = coords.map((polygonCoords: any) =>
          polygonCoords[0].map((coord: number[]) => [coord[1], coord[0]])
        );
        layer = L.polygon(polygons, {
          color: '#1890ff',
          weight: 2,
          fillOpacity: 0.1,
          fillColor: '#1890ff',
        });
      }

      if (layer) {
        const props = feature.properties || {};
        const tooltipContent = `
          <div style="font-size: 12px;">
            <strong>Parcela Auditada</strong><br/>
            ${props.name ? `Nombre: ${props.name}<br/>` : ''}
            ${props.surface ? `Superficie: ${props.surface.toFixed(2)} ha` : ''}
          </div>
        `;
        layer.bindTooltip(tooltipContent);
        layerGroup.addLayer(layer);
      }
    });

    if (layerGroup.getLayers().length > 0) {
      const bounds = layerGroup.getBounds();
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      }
    }
  }, [referencePlots, map]);

  return null;
};

/**
 * Component to display control points
 */
const ControlPointsLayer = ({ 
  controlPoints,
  layerRef
}: { 
  controlPoints: GeoJSON.FeatureCollection | null;
  layerRef?: React.MutableRefObject<L.FeatureGroup | null>;
}) => {
  const map = useMap();
  const layerGroupRef = useRef<L.FeatureGroup>(new L.FeatureGroup());

  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    
    if (layerRef) {
      layerRef.current = layerGroup;
    }
    
    map.addLayer(layerGroup);

    return () => {
      map.removeLayer(layerGroup);
    };
  }, [map, layerRef]);

  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    layerGroup.clearLayers();

    if (!controlPoints || !controlPoints.features) return;

    controlPoints.features.forEach((feature) => {
      if (!feature.geometry) return;

      const geometry = feature.geometry;
      const props = feature.properties || {};
      let layer: L.Layer | null = null;

      if (geometry.type === 'Point') {
        const coords = (geometry as any).coordinates;
        // Create red marker for control points
        const redIcon = L.divIcon({
          className: 'custom-control-point-marker',
          html: `<div style="
            background-color: #ff4d4f;
            width: 24px;
            height: 24px;
            border-radius: 50% 50% 50% 0;
            border: 2px solid white;
            transform: rotate(-45deg);
            box-shadow: 0 2px 5px rgba(0,0,0,0.3);
          "></div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 24],
          popupAnchor: [0, -24]
        });
        
        layer = L.marker([coords[1], coords[0]], {
          icon: redIcon,
        });
      } else if (geometry.type === 'Polygon') {
        const coords = (geometry as any).coordinates[0];
        layer = L.polygon(
          coords.map((coord: number[]) => [coord[1], coord[0]]),
          {
            color: '#ff4d4f',
            weight: 2,
            fillOpacity: 0.2,
            fillColor: '#ff4d4f',
          }
        );
      }

      if (layer) {
        const tooltipContent = `
          <div style="font-size: 12px;">
            <strong>Punto de Control</strong><br/>
            ${props.name ? `Nombre: ${props.name}<br/>` : ''}
            ${props.reason_name ? `Motivo: ${props.reason_name}` : ''}
          </div>
        `;
        layer.bindTooltip(tooltipContent);
        layerGroup.addLayer(layer);
      }
    });
  }, [controlPoints, map]);

  return null;
};

/**
 * Component to display existing geometry
 */
const GeometryDisplay = ({ 
  geometry,
  drawnItems 
}: { 
  geometry: GeoJSON.Geometry | null;
  drawnItems: L.FeatureGroup;
}) => {
  const map = useMap();

  useEffect(() => {
    console.log('🗺️ GeometryDisplay useEffect - geometry:', geometry);
    
    if (!geometry) {
      console.log('⚠️ No geometry to display');
      return;
    }

    drawnItems.clearLayers();

    let layer: L.Layer | null = null;

    if (geometry.type === "Point") {
      console.log('📍 Creating Point marker');
      const coords = (geometry as any).coordinates;
      layer = L.marker([coords[1], coords[0]], {
        icon: new L.Icon({
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        }),
      });
    } else if (geometry.type === "Polygon") {
      console.log('🔷 Creating Polygon');
      const coords = (geometry as any).coordinates[0];
      layer = L.polygon(
        coords.map((coord: number[]) => [coord[1], coord[0]]),
        {
          color: "#722ed1",
          weight: 3,
          fillOpacity: 0.3,
          fillColor: "#722ed1",
        }
      );
    }

    if (layer) {
      console.log('✅ Adding layer to map');
      drawnItems.addLayer(layer);

      const bounds = drawnItems.getBounds();
      if (bounds.isValid()) {
        console.log('🎯 Fitting bounds to geometry');
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      }
    } else {
      console.log('❌ No layer created for geometry type:', geometry.type);
    }
  }, [map, geometry, drawnItems]);

  return null;
};

/**
 * Component to invalidate map size after mount
 */
const MapSizeInvalidator = () => {
  const map = useMap();

  useEffect(() => {
    // Invalidate size after a short delay to ensure proper rendering
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 100);

    return () => clearTimeout(timer);
  }, [map]);

  return null;
};

/**
 * Component to add coordinate search control
 */
const CoordinateSearchControl = () => {
  const map = useMap();
  const { t } = useTranslation();

  useEffect(() => {
    const CoordControl = L.Control.extend({
      options: {
        position: "topright",
      },

      onAdd: function (map: L.Map) {
        const container = L.DomUtil.create(
          "div",
          "leaflet-bar leaflet-control leaflet-control-custom"
        );
        container.style.backgroundColor = "white";
        container.style.padding = "8px";
        container.style.borderRadius = "4px";
        container.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";

        const input = L.DomUtil.create("input", "", container);
        input.type = "text";
        input.placeholder = t("map.search_input_placeholder") || "Ciudad o Lat, Lng";
        input.style.width = "220px";
        input.style.padding = "4px 8px";
        input.style.border = "1px solid #d9d9d9";
        input.style.borderRadius = "2px";
        input.style.marginRight = "4px";

        const button = L.DomUtil.create("button", "", container);
        button.innerHTML = t("map.search_button") || "🔍 Buscar";
        button.style.padding = "4px 12px";
        button.style.backgroundColor = "#1890ff";
        button.style.color = "white";
        button.style.border = "none";
        button.style.borderRadius = "2px";
        button.style.cursor = "pointer";

        const handleCenter = async () => {
          const value = input.value.trim();

          if (!value) {
            message.warning(t("map.search_no_input") || "Por favor ingrese una ubicación o coordenadas");
            return;
          }

          const parts = value.split(",").map((p) => p.trim());

          if (parts.length === 2) {
            const lat = parseFloat(parts[0]);
            const lng = parseFloat(parts[1]);

            if (!isNaN(lat) && !isNaN(lng)) {
              if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                message.error(t("map.search_invalid_coords") || "Coordenadas fuera de rango válido");
                return;
              }

              map.setView([lat, lng], 16);
              message.success(t("map.search_centered") || "Mapa centrado en las coordenadas");
              return;
            }
          }

          button.disabled = true;
          button.innerHTML = t("map.search_button_loading") || "⏳ Buscando...";

          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
                value
              )}&countrycodes=es&limit=1`
            );

            if (!response.ok) {
              throw new Error("Error en la búsqueda");
            }

            const data = await response.json();

            if (data.length === 0) {
              message.error(
                t("map.search_not_found") || "No se encontró la ubicación"
              );
              return;
            }

            const result = data[0];
            const lat = parseFloat(result.lat);
            const lng = parseFloat(result.lon);

            map.setView([lat, lng], 14);
            message.success(`${t("map.search_location_found") || "Ubicación encontrada"}: ${result.display_name}`);
          } catch (error) {
            message.error(t("map.search_error") || "Error al buscar la ubicación");
            console.error("Geocoding error:", error);
          } finally {
            button.disabled = false;
            button.innerHTML = t("map.search_button") || "🔍 Buscar";
          }
        };

        button.onclick = handleCenter;
        input.onkeypress = (e) => {
          if (e.key === "Enter") {
            handleCenter();
          }
        };

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      },
    });

    const control = new CoordControl();
    map.addControl(control);

    return () => {
      map.removeControl(control);
    };
  }, [map, t]);

  return null;
};

/**
 * Component to add layer control
 */
const LayerToggleControl = ({
  referencePlotsLayer,
  controlPointsLayer,
  adjacentCropLayer,
}: {
  referencePlotsLayer: L.FeatureGroup | null;
  controlPointsLayer: L.FeatureGroup | null;
  adjacentCropLayer: L.FeatureGroup | null;
}) => {
  const map = useMap();
  const controlRef = useRef<L.Control | null>(null);

  useEffect(() => {
    // Always create the control, even if layers are null initially
    const LayerControl = L.Control.extend({
      options: {
        position: "topright",
      },

      onAdd: function (mapInstance: L.Map) {
        const container = L.DomUtil.create(
          "div",
          "leaflet-bar leaflet-control leaflet-control-layers"
        );
        container.style.backgroundColor = "white";
        container.style.padding = "10px";
        container.style.borderRadius = "4px";
        container.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
        container.style.minWidth = "200px";

        const title = L.DomUtil.create("div", "", container);
        title.innerHTML = "<strong>Capas del Mapa</strong>";
        title.style.marginBottom = "10px";
        title.style.fontSize = "14px";
        title.style.fontWeight = "bold";
        title.style.borderBottom = "1px solid #e8e8e8";
        title.style.paddingBottom = "8px";

        let hasLayers = false;

        if (referencePlotsLayer) {
          hasLayers = true;
          const refDiv = L.DomUtil.create("div", "", container);
          refDiv.style.marginBottom = "6px";
          refDiv.style.display = "flex";
          refDiv.style.alignItems = "center";

          const refCheckbox = L.DomUtil.create("input", "", refDiv) as HTMLInputElement;
          refCheckbox.type = "checkbox";
          refCheckbox.checked = true;
          refCheckbox.style.marginRight = "8px";
          refCheckbox.style.cursor = "pointer";
          refCheckbox.id = "ref-plots-checkbox";

          const refLabel = L.DomUtil.create("label", "", refDiv);
          refLabel.innerHTML = "Parcelas Auditadas";
          refLabel.style.cursor = "pointer";
          refLabel.style.fontSize = "13px";
          refLabel.htmlFor = "ref-plots-checkbox";

          L.DomEvent.on(refCheckbox, 'change', function(e: Event) {
            L.DomEvent.stopPropagation(e);
            const checkbox = e.target as HTMLInputElement;
            
            if (checkbox.checked) {
              if (!mapInstance.hasLayer(referencePlotsLayer)) {
                mapInstance.addLayer(referencePlotsLayer);
              }
            } else {
              if (mapInstance.hasLayer(referencePlotsLayer)) {
                mapInstance.removeLayer(referencePlotsLayer);
              }
            }
          });
        }

        if (controlPointsLayer) {
          hasLayers = true;
          const cpDiv = L.DomUtil.create("div", "", container);
          cpDiv.style.marginBottom = "6px";
          cpDiv.style.display = "flex";
          cpDiv.style.alignItems = "center";

          const cpCheckbox = L.DomUtil.create("input", "", cpDiv) as HTMLInputElement;
          cpCheckbox.type = "checkbox";
          cpCheckbox.checked = true;
          cpCheckbox.style.marginRight = "8px";
          cpCheckbox.style.cursor = "pointer";
          cpCheckbox.id = "cp-checkbox";

          const cpLabel = L.DomUtil.create("label", "", cpDiv);
          cpLabel.innerHTML = "Puntos de Control";
          cpLabel.style.cursor = "pointer";
          cpLabel.style.fontSize = "13px";
          cpLabel.htmlFor = "cp-checkbox";

          L.DomEvent.on(cpCheckbox, 'change', function(e: Event) {
            L.DomEvent.stopPropagation(e);
            const checkbox = e.target as HTMLInputElement;
            
            if (checkbox.checked) {
              if (!mapInstance.hasLayer(controlPointsLayer)) {
                mapInstance.addLayer(controlPointsLayer);
              }
            } else {
              if (mapInstance.hasLayer(controlPointsLayer)) {
                mapInstance.removeLayer(controlPointsLayer);
              }
            }
          });
        }

        // Show message if no layers available
        if (!hasLayers) {
          const noLayersDiv = L.DomUtil.create("div", "", container);
          noLayersDiv.innerHTML = "No hay capas disponibles";
          noLayersDiv.style.fontSize = "12px";
          noLayersDiv.style.color = "#999";
          noLayersDiv.style.fontStyle = "italic";
          noLayersDiv.style.marginTop = "8px";
        }

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      },
    });

    const control = new LayerControl();
    controlRef.current = control;
    map.addControl(control);

    return () => {
      if (controlRef.current) {
        map.removeControl(controlRef.current);
        controlRef.current = null;
      }
    };
  }, [map, referencePlotsLayer, controlPointsLayer, adjacentCropLayer]);

  return null;
};

/**
 * AdjacentCropMapEditor Component
 * Map component for viewing and editing adjacent crop geometry (Point or Polygon)
 */
export const AdjacentCropMapEditor = ({
  geometry,
  onGeometryChange,
  editable = true,
  referencePlots = null,
  controlPoints = null,
  height = "600px",
}: AdjacentCropMapEditorProps) => {
  const referencePlotsLayerRef = useRef<L.FeatureGroup | null>(null);
  const controlPointsLayerRef = useRef<L.FeatureGroup | null>(null);
  const adjacentCropLayerRef = useRef<L.FeatureGroup | null>(null);

  const getCenter = (): [number, number] => {
    if (!geometry) {
      if (referencePlots && referencePlots.features && referencePlots.features.length > 0) {
        const firstFeature = referencePlots.features[0];
        if (firstFeature.geometry) {
          const geom = firstFeature.geometry;
          if (geom.type === 'Polygon') {
            const coords = (geom as any).coordinates[0][0];
            return [coords[1], coords[0]];
          } else if (geom.type === 'MultiPolygon') {
            const coords = (geom as any).coordinates[0][0][0];
            return [coords[1], coords[0]];
          }
        }
      }
      return [40.0, -4.0];
    }

    if (geometry.type === "Point") {
      const coords = (geometry as any).coordinates;
      return [coords[1], coords[0]];
    }

    const coords: any = (geometry as any).coordinates;
    
    if (!coords) {
      return [40.0, -4.0];
    }

    let coordArray: any = coords;
    
    if (geometry.type === "Polygon" && Array.isArray(coords) && coords.length > 0) {
      coordArray = coords[0];
    }

    if (Array.isArray(coordArray) && coordArray.length > 0) {
      let sumLat = 0;
      let sumLng = 0;
      let count = 0;

      coordArray.forEach((coord: any) => {
        if (Array.isArray(coord) && coord.length >= 2) {
          sumLng += coord[0];
          sumLat += coord[1];
          count++;
        }
      });

      if (count > 0) {
        return [sumLat / count, sumLng / count];
      }
    }

    return [40.0, -4.0];
  };

  return (
    <div
      className="adjacent-crop-map-editor"
      style={{ position: "relative", height, width: "100%" }}
    >
      <style>{`
        /* Render the top-right controls (search + base-layer picker)
           on a single horizontal line, same layout as the plot map
           editor. */
        .adjacent-crop-map-editor .leaflet-top.leaflet-right {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          gap: 6px;
        }
        .adjacent-crop-map-editor .leaflet-top.leaflet-right > .leaflet-control {
          margin: 10px 0 0 0;
          float: none;
          clear: none;
        }
        .adjacent-crop-map-editor .leaflet-top.leaflet-right > .leaflet-control:last-child {
          margin-right: 10px;
        }
      `}</style>
      <MapContainer
        center={getCenter()}
        zoom={geometry ? 15 : (referencePlots ? 14 : 6)}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
      >
        {/* Coordinate search — rendered first so it sits to the
            left of the base-layer picker on the same row. */}
        <CoordinateSearchControl />

        {/* Base layers — same picker as the plot map / audit detail. */}
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Satélite (ArcGIS)">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="&copy; Esri"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satélite (Google)">
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
              attribution="&copy; Google"
              maxZoom={20}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="OpenStreetMap">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="IGN Base">
            <TileLayer
              url="https://tms-ign-base.idee.es/1.0.0/IGNBaseTodo/{z}/{x}/{-y}.jpeg"
              attribution="&copy; IGN"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {/* Reference Plots Layer */}
        {referencePlots && <ReferencePlotsLayer referencePlots={referencePlots} layerRef={referencePlotsLayerRef} />}

        {/* Control Points Layer */}
        {controlPoints && <ControlPointsLayer controlPoints={controlPoints} layerRef={controlPointsLayerRef} />}

        {/* Drawing Controls */}
        {editable && <DrawControls onGeometryChange={onGeometryChange} geometry={geometry} layerRef={adjacentCropLayerRef} />}
        
        {/* Map Size Invalidator */}
        <MapSizeInvalidator />
      </MapContainer>
    </div>
  );
};
